import { useState } from "react";
import toast from "react-hot-toast";
import { X } from "lucide-react";
import { useCreatePayment, PaymentAllocationPayload, PaymentMethod, PAYMENT_METHOD_LABEL } from "@/api/payments";
import { useInvoices, invoiceBalance } from "@/api/invoices";
import { useClients, displayName } from "@/api/clients";
import { getApiErrorMessage } from "@/api/client";

const METHOD_OPTIONS = Object.keys(PAYMENT_METHOD_LABEL) as PaymentMethod[];

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

export default function RegisterPaymentModal({ clientId: fixedClientId, onClose }: { clientId?: string; onClose: () => void }) {
  const createPayment = useCreatePayment();
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState<PaymentMethod>("TRANSFERENCIA");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState<{ id: string; name: string } | null>(null);
  const [allocationAmounts, setAllocationAmounts] = useState<Record<string, string>>({});

  const { data: clientResults } = useClients({ search: clientSearch, pageSize: 6 });
  const effectiveClientId = fixedClientId ?? selectedClient?.id;

  const { data: invoiceData } = useInvoices({ clientId: effectiveClientId, pageSize: 100 });
  const payableInvoices = (invoiceData?.data ?? []).filter((inv) => ["EMITIDA", "PARCIAL", "VENCIDA"].includes(inv.status));

  const totalAllocated = Object.values(allocationAmounts).reduce((sum, v) => sum + (Number(v) || 0), 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!effectiveClientId) {
      toast.error("Debe seleccionar un cliente.");
      return;
    }
    const allocations: PaymentAllocationPayload[] = Object.entries(allocationAmounts)
      .filter(([, amount]) => Number(amount) > 0)
      .map(([invoiceId, amount]) => ({ invoiceId, amount }));

    if (allocations.length === 0) {
      toast.error("Asigne el pago a al menos una factura.");
      return;
    }

    try {
      await createPayment.mutateAsync({
        clientId: effectiveClientId,
        paymentDate,
        amount: totalAllocated,
        method,
        reference: reference || undefined,
        notes: notes || undefined,
        allocations,
      });
      toast.success("Pago registrado correctamente.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Registrar pago</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          {!fixedClientId && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Cliente</label>
              {selectedClient ? (
                <div className="mt-1 flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm">
                  <span>{selectedClient.name}</span>
                  <button
                    type="button"
                    className="text-xs text-gray-400 hover:text-red-600"
                    onClick={() => {
                      setSelectedClient(null);
                      setAllocationAmounts({});
                    }}
                  >
                    Quitar
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    className="input-field mt-1"
                    placeholder="Buscar cliente..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    required
                  />
                  {clientSearch && clientResults?.data && clientResults.data.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                      {clientResults.data.map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                          onClick={() => {
                            setSelectedClient({ id: c.id, name: displayName(c) });
                            setClientSearch("");
                          }}
                        >
                          {displayName(c)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Fecha del pago</label>
              <input type="date" className="input-field mt-1" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Método</label>
              <select className="input-field mt-1" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
                {METHOD_OPTIONS.map((m) => (
                  <option key={m} value={m}>
                    {PAYMENT_METHOD_LABEL[m]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Referencia (opcional)</label>
              <input className="input-field mt-1" value={reference} onChange={(e) => setReference(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Notas (opcional)</label>
              <input className="input-field mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Facturas pendientes</label>
            {!effectiveClientId && <p className="mt-1 text-xs text-gray-400">Seleccione un cliente para ver sus facturas pendientes.</p>}
            {effectiveClientId && payableInvoices.length === 0 && (
              <p className="mt-1 text-xs text-gray-400">Este cliente no tiene facturas pendientes de pago.</p>
            )}
            <div className="mt-2 space-y-2">
              {payableInvoices.map((invoice) => {
                const balance = invoiceBalance(invoice);
                return (
                  <div key={invoice.id} className="flex items-center justify-between rounded-md border border-gray-100 p-2 text-xs">
                    <div>
                      <p className="font-medium text-gray-900">N.° {invoice.invoiceNumber}</p>
                      <p className="text-gray-500">Saldo pendiente: {currency.format(balance)}</p>
                    </div>
                    <input
                      type="number"
                      min="0"
                      max={balance}
                      step="0.01"
                      placeholder="Monto a aplicar"
                      className="input-field w-32 text-xs"
                      value={allocationAmounts[invoice.id] ?? ""}
                      onChange={(e) => setAllocationAmounts((prev) => ({ ...prev, [invoice.id]: e.target.value }))}
                    />
                  </div>
                );
              })}
            </div>
            <div className="mt-2 flex justify-end text-xs font-medium text-gray-700">
              <span>Total a registrar: {currency.format(totalAllocated)}</span>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={createPayment.isPending || totalAllocated <= 0}>
              {createPayment.isPending ? "Registrando..." : "Registrar pago"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
